/**
 * Reports — Per-Leg P&L (Mens / Womens / Ground 2 / Consolidated).
 *
 * Single backend call (`GET /api/reports/pnl?tournament=…`) returns ALL slices;
 * we just pick the right one based on the active tab. This keeps the page
 * snappy and consistent — switching tabs doesn't trigger a refetch.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { BarChart3, TrendingUp, TrendingDown, Info, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEG_TABS = [
  { key: 'consolidated', label: 'Consolidated', tone: 'slate' },
  { key: 'mens', label: 'Mens', tone: 'sky' },
  { key: 'womens', label: 'Womens', tone: 'rose' },
  { key: 'ground2', label: 'Ground 2', tone: 'amber' },
];

const fmt = (n) => {
  const v = Number(n || 0);
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const SignedAmount = ({ value, invert = false }) => {
  // Profit lines: positive = green (good), negative = red. For expense rows
  // we never want this; only call this for profit numbers.
  const v = Number(value || 0);
  const positive = invert ? v < 0 : v >= 0;
  return (
    <span
      className={`font-mono font-semibold tabular-nums ${
        positive ? 'text-emerald-700' : 'text-rose-700'
      }`}
    >
      {fmt(v)}
    </span>
  );
};

const Row = ({ label, value, sub, accent, testid }) => (
  <div
    className="flex items-baseline justify-between py-2 border-b border-border/40 last:border-b-0"
    data-testid={testid}
  >
    <div>
      <div className={`text-sm ${accent ? 'font-semibold' : ''}`}>{label}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
    <div
      className={`font-mono tabular-nums ${
        accent ? 'text-base font-bold' : 'text-sm'
      }`}
    >
      {value}
    </div>
  </div>
);

const Reports = () => {
  const [tournaments, setTournaments] = useState([]);
  const [tournament, setTournament] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load tournaments + pick the default
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/tournaments`, { withCredentials: true });
        const list = res?.data?.tournaments || [];
        setTournaments(list);
        const def =
          list.find((t) => t.is_default && t.is_active) ||
          list.find((t) => t.is_default) ||
          list[0];
        if (def?.name) setTournament(def.name);
      } catch (e) {
        console.warn('Reports: tournament list fetch failed:', e?.response?.data || e?.message);
      }
    })();
  }, []);

  const fetchPnl = async (t) => {
    if (!t) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/pnl`, {
        withCredentials: true,
        params: { tournament: t },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load P&L');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPnl(tournament);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournament]);

  const slices = useMemo(() => {
    if (!data) return {};
    return {
      consolidated: data.consolidated,
      mens: data.per_leg.mens,
      womens: data.per_leg.womens,
      ground2: data.per_leg.ground2,
      common: data.per_leg.common,
    };
  }, [data]);

  const renderSlice = (slice, legKey) => {
    if (!slice) return null;
    const rev = slice.revenue;
    const exp = slice.expenses;
    const profit = slice.profit;
    const showRevenueZeroNotice =
      legKey !== 'consolidated' && legKey !== 'common' && rev.committed === 0;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue card */}
        <Card className="border-emerald-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-emerald-800">
              <TrendingUp className="h-4 w-4" /> Revenue
            </CardTitle>
            {showRevenueZeroNotice && (
              <CardDescription className="text-xs text-amber-700">
                Revenue isn't leg-attributed yet — see the <strong>Common / Unallocated</strong> tab
                or use Consolidated for totals.
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="pt-0">
            <Row label="Committed" sub="Sales Orders raised" value={fmt(rev.committed)} />
            <Row label="Invoiced" sub="Revenue invoices issued" value={fmt(rev.invoiced)} />
            <Row label="Received" sub="Receipts banked" value={fmt(rev.received)} accent />
          </CardContent>
        </Card>

        {/* Expense card */}
        <Card className="border-rose-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-rose-800">
              <TrendingDown className="h-4 w-4" /> Expenses
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <Row label="Budgeted" sub="from Budget Heads × leg ratios" value={fmt(exp.budgeted)} />
            <Row
              label="PO Committed"
              sub="Purchase Orders (gross, scaled by leg split)"
              value={fmt(exp.po_committed)}
            />
            <Row
              label="Invoiced"
              sub="Purchase invoices, leg-attributed via linked PO"
              value={fmt(exp.invoiced)}
            />
            <Row label="Paid" sub="Vendor payments, leg-attributed" value={fmt(exp.paid)} accent />
          </CardContent>
        </Card>

        {/* Profit summary spans both columns */}
        <Card className="lg:col-span-2 border-primary/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4" /> Profit Snapshot
            </CardTitle>
            <CardDescription className="text-xs">
              Multiple definitions of "profit" depending on the stage you care about.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider">
                  vs Budget
                </div>
                <div className="text-[10px] text-muted-foreground mb-1">
                  Committed revenue − Budget
                </div>
                <SignedAmount value={profit.vs_budget} />
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider">
                  vs PO
                </div>
                <div className="text-[10px] text-muted-foreground mb-1">
                  Committed revenue − POs
                </div>
                <SignedAmount value={profit.vs_po} />
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider">
                  vs Invoiced
                </div>
                <div className="text-[10px] text-muted-foreground mb-1">
                  Invoiced revenue − Invoiced expenses
                </div>
                <SignedAmount value={profit.vs_invoiced} />
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider font-semibold">
                  Cash P&L
                </div>
                <div className="text-[10px] text-muted-foreground mb-1">
                  Received − Paid
                </div>
                <SignedAmount value={profit.cash} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold font-heading flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-primary" />
            Reports
          </h1>
          <p className="text-muted-foreground mt-1">
            Per-leg P&L (Mens · Womens · Ground 2 · Consolidated) for the selected tournament.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={tournament || ''} onValueChange={setTournament}>
            <SelectTrigger className="w-60" data-testid="reports-tournament-select">
              <SelectValue placeholder="Select tournament" />
            </SelectTrigger>
            <SelectContent>
              {tournaments.map((t) => (
                <SelectItem key={t.id || t.name} value={t.name}>
                  {t.name}
                  {t.is_default ? ' (default)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={() => fetchPnl(tournament)}
            disabled={loading}
            title="Refresh"
            data-testid="reports-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {data?.notes?.revenue && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="py-2 px-3 text-xs text-amber-900 flex items-start gap-2">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <strong>Note:</strong> {data.notes.revenue} {data.notes.po_invoice_paid}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="consolidated">
        <TabsList className="w-full grid grid-cols-5">
          {LEG_TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`reports-tab-${t.key}`}>
              {t.label}
            </TabsTrigger>
          ))}
          <TabsTrigger value="common" data-testid="reports-tab-common">
            Common / Unallocated
          </TabsTrigger>
        </TabsList>

        {LEG_TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-4">
            {loading ? (
              <div className="py-16 text-center text-muted-foreground text-sm">Loading…</div>
            ) : (
              renderSlice(slices[t.key], t.key)
            )}
          </TabsContent>
        ))}
        <TabsContent value="common" className="mt-4">
          {loading ? (
            <div className="py-16 text-center text-muted-foreground text-sm">Loading…</div>
          ) : (
            renderSlice(slices.common, 'common')
          )}
        </TabsContent>
      </Tabs>

      {/* Department breakdown */}
      {data?.department_rows?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Department breakdown</CardTitle>
            <CardDescription className="text-xs">
              Budgeted vs PO Committed across departments, sliced by leg.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left py-2 px-3">Department</th>
                  <th className="text-right py-2 px-3">Budget · Mens</th>
                  <th className="text-right py-2 px-3">Budget · Womens</th>
                  <th className="text-right py-2 px-3">Budget · Ground 2</th>
                  <th className="text-right py-2 px-3">Budget · Common</th>
                  <th className="text-right py-2 px-3 border-l border-border">PO · Mens</th>
                  <th className="text-right py-2 px-3">PO · Womens</th>
                  <th className="text-right py-2 px-3">PO · Ground 2</th>
                  <th className="text-right py-2 px-3">PO · Common</th>
                </tr>
              </thead>
              <tbody>
                {data.department_rows.map((r) => (
                  <tr key={r.department} className="border-t border-border/40 hover:bg-muted/30">
                    <td className="py-2 px-3 font-medium">{r.department}</td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums">{fmt(r.budgeted.mens)}</td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums">{fmt(r.budgeted.womens)}</td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums">{fmt(r.budgeted.ground2)}</td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums">{fmt(r.budgeted.common)}</td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums border-l border-border">{fmt(r.po_committed.mens)}</td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums">{fmt(r.po_committed.womens)}</td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums">{fmt(r.po_committed.ground2)}</td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums">{fmt(r.po_committed.common)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Reports;
